import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../store"
import { useInView } from "react-intersection-observer"
import { RANGER_SUMMA } from "../../utils/defaultOptions"
import { Ranger } from "../../UI/Ranger"
import { PrimaryButton } from "../../UI/PrimaryButton"
import { Answer } from "../Answer"
import { NAV_LINK_1, RANGE_LINK } from "../../constants/constantApi"
import { getFormLink } from "../../utils/getTld"
import { updateMoney } from "../../store/features/mainData/mainDataSlice"
import { ISetActiveLink } from "../../hooks/useObserver"
import { useSendRangeDataAction } from "../../api/methods/sendUserAction/hooks"
import { sendToNextStepFormHandle } from "../../api/methods/sendUserAction/handlers"
import { Variants } from "../../configs/configs.types"
import heroBtnIcon from "../../assets/images/hero_btn_icon_v2.svg"
import heroV2 from "../../assets/images/hero-v2.png"
import heroBgV2 from "../../assets/images/hero-bg-v2.svg"
import heroV1 from "../../assets/images/hero_v1.png"
import heroV1Entrepreneurs from "../../assets/images/hero_v1_entrepreneurs.png"
import { getFormLinkWithParams } from "../../utils/getLinkWithSearchParams"
import { IRangeDataDescription } from "../../UI/Ranger/Ranger.types"
import { metaPageConfigs } from "../../configs/metaPageConfigs"
import { ENTREPRENEURS_SLUG, entrepreneursOverrides } from "../../configs/entrepreneursOverrides"
import "./hero.sass"
import { getSummaProps } from "../../utils/getSummaProps"
import { getMetaPageConfig } from "../../utils/getMetaPageConfig"
import { getTitleWithVariant } from "../../utils/getConfigVariants"
import { EstimatedPayment } from "../../UI/EstimatedPayment"
import { MX_ESTIMATED_PAYMENT_MONTHS } from "../../utils/estimateMonthlyPayment"
import checkIcon from "../../assets/images/check.svg"

const ENTREPRENEURS_FAVICON = "/favicon-entrepreneurs.ico?v=1"

export function Hero({ setActive }: ISetActiveLink) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [isWithinRange, setIsWithinRange] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1199.5px)")
    const handleChange = (e: MediaQueryListEvent) => setIsWithinRange(e.matches)
    setIsWithinRange(mediaQuery.matches)
    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [isWithinRange])

  const { ref, inView } = useInView({
    threshold: 0.2
  })

  const dataConfig = useAppSelector(state => state.configs.general.data?.main)
  const heroConfig = useAppSelector(state => state.configs.general.data?.hero)
  const commonConfig = useAppSelector(state => state.configs.general.data?.common)
  const { money, period } = useAppSelector(state => state.mainData)
  const clickID = useAppSelector(state => state.userData.clickID)

  const { isInitialMoney } = useSendRangeDataAction(money, period)

  const variant = process.env.REACT_APP_VARIANT ?? Variants.v0

  // Формируем ссылку на форму с абсолютным путем, чтобы она работала с SEO-страницами
  const formLinkParams = new URLSearchParams(window.location.search)
  formLinkParams.set("amount", String(money))
  const formLink = getFormLinkWithParams(getFormLink(), formLinkParams.toString())

  useEffect(() => {
    if (inView) setActive(NAV_LINK_1)
  }, [inView])

  function moneyChange(money: number) {
    dispatch(updateMoney(money))
    isInitialMoney.current = false
  }

  const showCompactAmountSelector = Boolean(dataConfig?.rangeBlock?.compactAmountSelector)
  const showEstimatedPayment = Boolean(dataConfig?.rangeBlock?.showEstimatedPayment)
  const estimatedPaymentBlock = showEstimatedPayment ? (
    <EstimatedPayment
      amount={money}
      months={dataConfig?.rangeBlock?.estimatedPaymentMonths ?? MX_ESTIMATED_PAYMENT_MONTHS}
      markup={dataConfig?.rangeBlock?.estimatedPaymentMarkup}
      currency={commonConfig?.rangeData?.summa?.currency ?? "$"}
      currencyPosition={
        commonConfig?.rangeData?.summa?.currencyPosition === "suffix" ? "suffix" : "prefix"
      }
      label={dataConfig?.rangeBlock?.estimatedPaymentLabel ?? "Cuota estimada"}
      amountLabel={dataConfig?.rangeBlock?.estimatedPaymentAmountLabel ?? "Cantidad"}
      totalLabel={dataConfig?.rangeBlock?.estimatedPaymentTotalLabel ?? "Total a pagar"}
      disclaimer={dataConfig?.rangeBlock?.estimatedPaymentDisclaimer}
    />
  ) : null

  // function periodChange(period: number) {
  //   dispatch(updatePeriod(period));
  // }

  function onOpenFormHandler() {
    if (clickID) {
      sendToNextStepFormHandle(clickID, 0)
    }

    navigate(formLink)
  }

  const location = useLocation()
  const currentPath = location.pathname.split("/").pop() //получаем "dlouhodoba-pujcka.html"
  const isEntrepreneursPage = currentPath === ENTREPRENEURS_SLUG
  const metaConfig = currentPath ? metaPageConfigs[currentPath] : null
  const metaConfigWithVariant = getMetaPageConfig(metaConfig, variant)

  const title = isEntrepreneursPage
    ? entrepreneursOverrides.heroTitle
    : metaConfigWithVariant?.title ||
    getTitleWithVariant(dataConfig?.title || "", dataConfig?.titleVariants)
  const description = metaConfigWithVariant?.description || ""
  const ogImage = isEntrepreneursPage ? "/logo512-entrepreneurs.png" : "/logo512.png"
  const buttonText = isEntrepreneursPage
    ? entrepreneursOverrides.primaryButtonText
    : commonConfig?.primaryButtonText ?? ""

  // Patches <head> directly when entering the entrepreneurs page and restores everything on unmount (route change).
  useEffect(() => {
    if (!isEntrepreneursPage || !metaConfigWithVariant) return

    // 1. Swap favicon
    const faviconEls = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
    const prevFavicons = new Map(Array.from(faviconEls).map(el => [el, el.href]))
    prevFavicons.forEach((prev, el) => { el.href = ENTREPRENEURS_FAVICON })

    // 2. Set page-specific browser tab title
    const prevTitle = document.title
    document.title = metaConfigWithVariant.title

    // 3. Update existing <meta name="description">
    const metaDescEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    const prevDesc = metaDescEl?.getAttribute("content")
    if (metaDescEl && metaConfigWithVariant.metaDescription) {
      metaDescEl.setAttribute("content", metaConfigWithVariant.metaDescription)
    }

    // 4. Create <meta property="og:image"> if absent, then set value
    let ogImgEl = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null
    const ogImgCreated = !ogImgEl
    const prevOgImg = ogImgEl?.getAttribute("content")
    if (!ogImgEl) {
      ogImgEl = document.createElement("meta")
      ogImgEl.setAttribute("property", "og:image")
      document.head.appendChild(ogImgEl)
    }
    ogImgEl.setAttribute("content", ogImage)

    // Restore all changes when navigating away
    return () => {
      prevFavicons.forEach((prev, el) => { el.href = prev })
      document.title = prevTitle
      if (metaDescEl && prevDesc !== undefined) metaDescEl.setAttribute("content", prevDesc ?? "")
      if (ogImgCreated && ogImgEl) {
        ogImgEl.remove()
      } else if (ogImgEl && prevOgImg !== undefined) {
        ogImgEl.setAttribute("content", prevOgImg ?? "")
      }
    }
  }, [isEntrepreneursPage, metaConfigWithVariant, ogImage])

  // v1-specific SEO landing pages (metaPageConfigs) override the default title/og tags.
  // Patched imperatively (not via <title>/<meta> JSX) so it doesn't race with the default
  // ones rendered once at the app root (src/index.tsx) — React doesn't dedupe repeated
  // <title>/<meta> tags rendered at the same time, so two declarative sources would both
  // land in <head> with undefined precedence.
  useEffect(() => {
    if (variant !== "v1" || !metaConfigWithVariant) return

    const prevTitle = document.title
    document.title = metaConfigWithVariant.title

    const metaDescEl = document.querySelector('meta[name="description"]') as HTMLMetaElement | null
    const prevDesc = metaDescEl?.getAttribute("content")
    if (metaDescEl && metaConfigWithVariant.metaDescription) {
      metaDescEl.setAttribute("content", metaConfigWithVariant.metaDescription)
    }

    const ogTitleEl = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null
    const prevOgTitle = ogTitleEl?.getAttribute("content")
    ogTitleEl?.setAttribute("content", metaConfigWithVariant.title)

    const ogDescEl = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null
    const prevOgDesc = ogDescEl?.getAttribute("content")
    if (ogDescEl && metaConfigWithVariant.metaDescription) {
      ogDescEl.setAttribute("content", metaConfigWithVariant.metaDescription)
    }

    const ogImgEl = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null
    const prevOgImg = ogImgEl?.getAttribute("content")
    ogImgEl?.setAttribute("content", ogImage)

    return () => {
      document.title = prevTitle
      if (metaDescEl && prevDesc !== undefined) metaDescEl.setAttribute("content", prevDesc ?? "")
      if (ogTitleEl && prevOgTitle !== undefined) ogTitleEl.setAttribute("content", prevOgTitle ?? "")
      if (ogDescEl && prevOgDesc !== undefined) ogDescEl.setAttribute("content", prevOgDesc ?? "")
      if (ogImgEl && prevOgImg !== undefined) ogImgEl.setAttribute("content", prevOgImg ?? "")
    }
  }, [metaConfigWithVariant, ogImage])

  if (variant === "v1") {
    return (
      <section className={`section hero hero__${variant}`} id={NAV_LINK_1}>
        {!isWithinRange ? (
          <div className="hero__container">
            <div className="hero__range" ref={ref} id={RANGE_LINK}>
              <h2 className="hero__range-title" id="main_header_qa">
                <p>{title}</p>
              </h2>
              <div className="hero__range__bar">
                <Ranger
                  props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
                  firstValue={money}
                  onChange={moneyChange}
                  showTypedInput={showCompactAmountSelector}
                />
              </div>
              {estimatedPaymentBlock}
              <PrimaryButton
                text={buttonText}
                onClick={onOpenFormHandler}
                id="send_amount_main"
              />
              {!!heroConfig?.v1?.description && (
                <span className="hero-description">{heroConfig.v1.description}</span>
              )}
            </div>
            <div className="hero-character-wrap">
              <img
                className="hero-character"
                src={isEntrepreneursPage ? heroV1Entrepreneurs : heroV1}
                alt="hero"
                width={600}
                height={600}
              />
            </div>
          </div>
        ) : (
          <div className="container hero__container">
            <div className="hero__range" ref={ref} id={RANGE_LINK}>
              <h2 className="hero__range-title" id="main_header_qa">
                <p>{title}</p>
              </h2>
            </div>
            <div className="hero-character-wrap">
              <img
                className="hero-character"
                src={isEntrepreneursPage ? heroV1Entrepreneurs : heroV1}
                alt="hero"
                width={600}
                height={600}
              />
            </div>
            <div className="hero__range__bar">
              <Ranger
                props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
                firstValue={money}
                onChange={moneyChange}
                showTypedInput={showCompactAmountSelector}
              />
            </div>
            {estimatedPaymentBlock}
            <PrimaryButton
              text={commonConfig?.primaryButtonText ?? ""}
              onClick={onOpenFormHandler}
              id="send_amount_main"
            />
            {!!heroConfig?.v1?.description && (
              <span className="hero-description">{heroConfig.v1.description}</span>
            )}
          </div>
        )}

        <div className="container hero__answer" id="answer">
          {description && (
            <div
              className="hero__answer-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <Answer />
        </div>
      </section>
    )
  }

  if (variant === "v2") {
    return (
      <section className={`section hero hero__${variant}`} id={NAV_LINK_1}>
        {!isWithinRange ? (
          <div className="container hero__container">
            <div className="hero__range" ref={ref} id={RANGE_LINK}>
              <h2 className="hero__range-title" id="main_header_qa">
                {title}
              </h2>
              <div className="range__bar">
                <Ranger
                  props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
                  firstValue={money}
                  onChange={moneyChange}
                  showTypedInput={showCompactAmountSelector}
                />
              </div>
              {estimatedPaymentBlock}
              <PrimaryButton
                text={buttonText}
                onClick={onOpenFormHandler}
                id="send_amount_main"
                children={<img className="hero-btn-pic" src={heroBtnIcon} alt="heroBtnIcon" />}
              />
              {!!heroConfig?.v2?.description && (
                <span className="hero-description">{heroConfig.v2.description}</span>
              )}
            </div>
            <img
              className="hero-circle-char"
              src={heroBgV2}
              alt="heroBG"
              width={347}
              height={347}
            />
            <img
              className="hero-character"
              src={heroV2}
              alt="hero-v2"
              width={330}
              height={400}
            />
          </div>
        ) : (
          <div className="container hero__container">
            <div className="hero__range" ref={ref} id={RANGE_LINK}>
              <div className="hero__range-text-container">
                <div className="hero__range-text">
                  <h2 className="hero__range-title" id="main_header_qa">
                    {title}
                  </h2>
                </div>

                <div className="range__bar">
                  <Ranger
                    props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
                    firstValue={money}
                    onChange={moneyChange}
                    showTypedInput={showCompactAmountSelector}
                  />
                </div>
              </div>

              <img
                className="hero-circle-char"
                src={heroBgV2}
                alt="heroBG"
                width={347}
                height={347}
              />
              <img
                className="hero-character"
                src={heroV2}
                alt="hero-v2"
                width={330}
                height={400}
              />
            </div>

            <div className="hero-btn-description">
              {estimatedPaymentBlock}
              <PrimaryButton
                text={buttonText}
                onClick={onOpenFormHandler}
                id="send_amount_main"
                children={<img className="hero-btn-pic" src={heroBtnIcon} alt="heroBtnIcon" />}
              />
              {!!heroConfig?.v2?.description && (
                <span className="hero-description">{heroConfig.v2.description}</span>
              )}
            </div>
          </div>
        )}

        <div className="container hero__answer" id="answer">
          {description && (
            <div
              className="hero__answer-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <Answer />
        </div>
      </section>
    )
  }

  if (variant === "v3") {
    return (
      <section className={`section hero hero__${variant}`} id={NAV_LINK_1}>
        <div className="hero__container">
          <div className="hero__range" ref={ref} id={RANGE_LINK}>
            <h2 className="hero__range-title" id="main_header_qa">
              {title}
            </h2>

            <div className="hero__range__bar">
              <Ranger
                props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
                firstValue={money}
                onChange={moneyChange}
                showTypedInput={showCompactAmountSelector}
              />
            </div>
            <div className="hero-btn-description">
              {estimatedPaymentBlock}
              <PrimaryButton
                text={buttonText}
                onClick={onOpenFormHandler}
                id="send_amount_main"
              />
              {!!heroConfig?.v3?.description && (
                <span className="hero-description">{heroConfig.v3.description}</span>
              )}
            </div>
          </div>
        </div>

        <div className="container hero__answer" id="answer">
          {description && (
            <div
              className="hero__answer-description"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
          <Answer />
        </div>
      </section>
    )
  }

  return (
    <section
      className={`section hero hero__${variant}${showCompactAmountSelector ? " hero--compact" : ""}`}
      id={NAV_LINK_1}
    >
      <h1 className="section__title hero__title_mobile" id="main_header_mobile_qa">
        {title}
      </h1>
      <div className="container hero__container">
        <img
          className="hero__image"
          src={dataConfig?.img.url}
          alt={dataConfig?.img.alt}
          fetchPriority="high"
        />
        <div className="hero__text">
          <h1 className="section__title hero__title_desktop" id="main_header_qa">
            {title}
          </h1>
          <ul className="hero__text-list">
            {(dataConfig?.description ?? []).map((item, index) => (
              <li className="hero__text-item" key={index}>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="hero__range" ref={ref} id={RANGE_LINK}>
          <div className="hero__range-text">
            <h2 className="hero__range-title">{dataConfig?.rangeBlock.title}</h2>
            <p className="hero__range-description">{dataConfig?.rangeBlock.description}</p>
          </div>
          <div className="range__bar">
            <Ranger
              props={getSummaProps(commonConfig?.rangeData?.summa ?? RANGER_SUMMA, variant)}
              firstValue={money}
              onChange={moneyChange}
              showTypedInput={showCompactAmountSelector}
            />
            {/* <Ranger
              props={commonConfig?.rangeData?.period ?? RANGER_PERIOD}
              firstValue={period}
              onChange={periodChange}
            /> */}
          </div>

          <PrimaryButton
            text={commonConfig?.primaryButtonText ?? ""}
            onClick={onOpenFormHandler}
            id="send_amount_main"
          />

          {estimatedPaymentBlock}

          {!!commonConfig?.rangeData?.description &&
            rangeDataDescriptionRender(commonConfig.rangeData.description)}
        </div>
        {showCompactAmountSelector && (
          <div className="hero__promo">
            <ul className="hero__promo-list">
              {(dataConfig?.description ?? []).map((item, index) => (
                <li className="hero__promo-item" key={index}>
                  <span className="hero__promo-check">
                    <img src={checkIcon} alt="" width={12} height={12} />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            {dataConfig?.img.url && (
              <img
                className="hero__promo-img"
                src={dataConfig.img.url}
                alt={dataConfig.img.alt}
              />
            )}
          </div>
        )}
      </div>
      <div className="container hero__answer" id="answer">
        {description && (
          <div
            className="hero__answer-description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        <Answer />
      </div>
    </section>
  )
}

const scrollToContacts = () => {
  const element = document.getElementById("contacts")
  if (element) {
    element.scrollIntoView({ behavior: "smooth" })
  }
}

const rangeDataDescriptionRender = (data: IRangeDataDescription) => (
  <p className="hero__description">
    {data.text}
    <a
      className="hero__description_link"
      href="#contacts"
      onClick={e => {
        e.preventDefault()
        scrollToContacts()
      }}
    >
      {data.link.text}
    </a>
  </p>
)
